<template>
    <div class="block-editor ai-classification payload" v-if="clone">
        <div class="mode-selector">
            <strong>{{ $t("chatbotv2.classificationMode") }}</strong>
            <div class="mode-buttons">
                <button
                    type="button"
                    :class="['mode-button', { active: (clone.mode || 'text') === 'text' }]"
                    @click="setMode('text')"
                >
                    <i class="material-icons">text_fields</i>
                    {{ $t("chatbotv2.textMode") }}
                </button>
                <button
                    type="button"
                    :class="['mode-button', { active: (clone.mode || 'text') === 'image' }]"
                    @click="setMode('image')"
                >
                    <i class="material-icons">image</i>
                    {{ $t("chatbotv2.imageMode") }}
                </button>
            </div>
        </div>

        <strong>{{ $t("message.expressionClassified") }}: </strong>
        <div class="says">
            <TiptapEditor
                :show-emoji="true"
                :show-attributes="true"
                :placeholder="$t('chatbotv2.classificationImageUrlPlaceholder')"
                v-model="clone.message"
                :key="'tiptap-prompt' + renderKey"
            />
        </div>

        <div v-if="!isTextMode">
            <strong>{{ $t("chatbotv2.aiClassificationImageUrlLabel") }}: </strong>
            <div class="says">
                <TiptapEditor
                    :show-emoji="false"
                    :show-attributes="true"
                    :placeholder="$t('chatbotv2.classificationImagePlaceholder')"
                    v-model="clone.imageUrl"
                    :key="'tiptap-image-url' + renderKey"
                />
            </div>
        </div>
        <strong>{{ $t("message.classification") }}:</strong>
        <div class="says" v-for="(tag, i) in clone.tags" :key="tag.id || i">
            <TiptapEditor
                :show-emoji="true"
                :show-attributes="true"
                :placeholder="$t('message.classification')"
                :value="tag.value || tag"
                @input="changeTags(i, $event)"
                :key="'editor-' + (tag.id || i)"
            />
            <SupsisButton danger @click="removeTag(i)" small>
                <span class="material-icons">delete</span>
            </SupsisButton>
        </div>
        <a-divider />
        <SupsisButton primary @click="addTag"
            ><span class="material-icons">add</span> {{ $t("message.classification") }}</SupsisButton
        >
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../common/TiptapEditor";
import log2 from "../../../../../../utils/log2";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

const log = log2("AIClassification");
export default {
    name: "AIClassification",
    components: { TiptapEditor, SupsisButton },
    props: {
        value: {
            type: Object,
            default: () => ({
                message: "",
                imageUrl: "",
                tags: [],
                mode: "text",
            }),
        },
    },
    data() {
        const cloned = this.normalizeClone(_.cloneDeep(this.value));
        return {
            clone: cloned,
            renderKey: 0,
            idCounter: 0, // ID çakışmasını önlemek için counter
        };
    },
    computed: {
        isTextMode() {
            return (this.clone.mode || "text") === "text";
        },
    },
    watch: {
        clone: {
            handler(newValue) {
                // Backend'e gönderirken tag objelerini string'e çevir
                const payload = {
                    message: newValue.message,
                    imageUrl: newValue.imageUrl,
                    mode: newValue.mode,
                    tags: newValue.tags.map((tag) => {
                        // Eğer obje formatındaysa sadece value'yu al
                        return typeof tag === "object" && tag.value ? tag.value : tag;
                    }),
                };
                this.$emit("input", payload);
            },
            deep: true,
        },
        /**
         * Parent'tan gelen value prop'u değiştiğinde clone'u güncelle
         * Bu watcher sonsuz loop'u önler
         */
        value: {
            handler(newValue) {
                if (newValue && JSON.stringify(newValue.tags) !== JSON.stringify(this.clone.tags.map(t => t.value || t))) {
                    // Sadece tags gerçekten değiştiyse güncelle
                    this.clone = this.normalizeClone(_.cloneDeep(newValue));
                }
            },
            deep: true,
        },
    },
    methods: {
        /**
         * Clone objesini normalize eder ve her tag'e unique ID atar
         * @param {Object} payload - Normalize edilecek payload objesi
         * @returns {Object} - Normalize edilmiş payload
         */
        normalizeClone(payload) {
            const normalized = payload || {};
            if (!normalized.mode) normalized.mode = "text";
            if (typeof normalized.imageUrl === "undefined") normalized.imageUrl = "";
            if (typeof normalized.message === "undefined") normalized.message = "";
            if (!Array.isArray(normalized.tags)) normalized.tags = [];
            
            // Eski string formatındaki tag'leri obje formatına çevir
            normalized.tags = normalized.tags.map((tag) => {
                if (typeof tag === "string") {
                    return {
                        id: this.generateUniqueId(),
                        value: tag,
                    };
                }
                // Zaten obje formatındaysa ID kontrolü yap
                if (!tag.id) {
                    tag.id = this.generateUniqueId();
                }
                return tag;
            });
            
            return normalized;
        },
        /**
         * Unique ID oluşturur (timestamp + counter + random)
         * Counter sayesinde aynı milisaniyede çakışma olmaz
         * @returns {string} - Unique ID
         */
        generateUniqueId() {
            this.idCounter++;
            return `tag_${Date.now()}_${this.idCounter}_${Math.random().toString(36).substr(2, 9)}`;
        },
        /**
         * Tag değerini günceller
         * @param {number} index - Tag index'i
         * @param {string} value - Yeni değer
         */
        changeTags(index, value) {
            this.clone = {
                message: this.clone.message,
                imageUrl: this.clone.imageUrl,
                mode: this.clone.mode,
                tags: this.clone.tags.map((tag, i) => {
                    if (index === i) {
                        // Obje formatında güncelle
                        return {
                            id: tag.id,
                            value: value,
                        };
                    }
                    return tag;
                }),
            };
        },
        /**
         * Yeni tag ekler
         */
        addTag() {
            this.clone.tags.push({
                id: this.generateUniqueId(),
                value: `Class ${this.clone.tags.length + 1}`,
            });
            this.renderKey++;
        },
        /**
         * Tag'i siler
         * @param {number} index - Silinecek tag'in index'i
         */
        removeTag(index) {
            this.clone.tags.splice(index, 1);
            // renderKey'i artırarak Vue'nun component'leri yeniden render etmesini sağla
            this.renderKey++;
        },
        setMode(mode) {
            this.clone.mode = mode;
            this.handleModeChange();
        },
        handleModeChange() {
            const mode = this.clone.mode || "text";
            this.clone.mode = mode;
            if (mode === "text") {
                if (!this.clone.message && this.value && this.value.message) {
                    this.clone.message = this.value.message;
                }
                this.clone.imageUrl = "";
            } else {
                if (!this.clone.imageUrl && this.value && this.value.imageUrl) {
                    this.clone.imageUrl = this.value.imageUrl;
                }
            }
        },
    },
};
</script>

<style lang="scss">
.block-editor.ai-classification {
    padding: 30px 30px 90px;

    .mode-selector {
        margin-bottom: 20px;

        strong {
            display: block;
            margin-bottom: 12px;
            color: #333;
            font-size: 14px;
        }

        .mode-buttons {
            display: flex;
            gap: 12px;

            .mode-button {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                border: 2px solid #e8e8e8;
                border-radius: 8px;
                background: #ffffff;
                color: #666;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                outline: none;

                .material-icons {
                    font-size: 18px;
                }

                &:hover {
                    border-color: #1890ff;
                    color: #1890ff;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
                }

                &.active {
                    border-color: #1890ff;
                    background: #1890ff;
                    color: white;
                    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);

                    &:hover {
                        background: #40a9ff;
                        border-color: #40a9ff;
                        transform: translateY(-1px);
                    }
                }
            }
        }
    }

    .says {
        display: flex;
        gap: 5px;
        align-items: center;
        flex-direction: row !important;

        .supsis-button {
            background: transparent !important;
            color: red !important;
            border: none !important;
        }
    }

    .ai-classification-header {
        align-items: center;
        display: flex;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        margin-bottom: 16px;

        .material-icons {
            margin-right: 6px;
        }
    }

    .editable-editor-wrapper {
        border: 1px solid white;
        border-radius: 13px;
        //max-height: 50px;

        .editable-editor {
            //max-height: 50px;

            .ProseMirror {
                //max-height: 50px;
                overflow-y: auto;
            }
        }

        &.focused {
            border-color: #006cff;
        }

        &:hover {
            border-color: #006cff;
        }
    }
}
</style>
